import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, AlertCircle, FileText, Loader2, Upload } from 'lucide-react';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { Chip } from '@/ui/primitives';
import { formatDateBR } from '@/lib/format';

// IDs vêm como string (BIGINT serializado pelo node-pg). Coerce pra number.
const checklistItemSchema = z.object({
  id: z.coerce.number(),
  bloco_codigo: z.string().nullish(),
  bloco_nome: z.string().nullish(),
  bloco_ordem: z.coerce.number().nullish(),
  nome_documento: z.string(),
  obrigatorio: z.boolean().nullish(),
  status: z.string().nullish(),
  validade: z.string().nullish(),
  vencimento: z.string().nullish(),
  data_recebimento: z.string().nullish(),
  documento_id: z.coerce.number().nullish(),
  arquivo_url: z.string().nullish(),
  observacoes: z.string().nullish(),
  normativo: z.string().nullish(),
  origem_documento: z.string().nullish(),
  responsavel_conferencia: z.string().nullish(),
  pendencia: z.string().nullish(),
  documento_origem_id: z.coerce.number().nullish(),
  documento_vinculado_id: z.coerce.number().nullish(),
  local_origem: z.string().nullish(),
  local_destino: z.string().nullish(),
  data_copia: z.string().nullish(),
  autor_movimentacao: z.string().nullish(),
});

type ChecklistItem = z.infer<typeof checklistItemSchema>;

const checklistResponseSchema = z.array(checklistItemSchema);

function statusTone(s: string | null | undefined): 'ok' | 'warn' | 'danger' | 'neutral' {
  switch (s) {
    case 'recebido':
    case 'aprovado':
    case 'ok':
    case 'validado':
      return 'ok';
    case 'pendente':
    case 'em_analise':
    case 'em_conferencia':
      return 'warn';
    case 'rejeitado':
    case 'vencido':
    case 'ilegivel':
    case 'duplicado':
      return 'danger';
    default:
      return 'neutral';
  }
}

function statusLabel(s: string | null | undefined): string {
  switch (s) {
    case 'recebido':
      return 'Recebido';
    case 'aprovado':
      return 'Aprovado';
    case 'validado':
      return 'Validado';
    case 'pendente':
      return 'Pendente';
    case 'em_analise':
      return 'Em análise';
    case 'em_conferencia':
      return 'Em conferência';
    case 'rejeitado':
      return 'Rejeitado';
    case 'vencido':
      return 'Vencido';
    case 'ilegivel':
      return 'Ilegível';
    case 'duplicado':
      return 'Duplicado';
    case 'nao_aplicavel':
      return 'Não aplicável';
    case 'dispensado':
      return 'Dispensado';
    default:
      return '—';
  }
}

interface Props {
  cardId: string;
}

export function ChecklistDocumentos({ cardId }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['credito-checklist', cardId],
    queryFn: ({ signal }) =>
      api(`/api/v2/processos-credito/${cardId}/checklist`, {
        schema: checklistResponseSchema,
        signal,
      }),
    enabled: !!cardId,
    staleTime: 30_000,
  });
  const upload = useMutation({
    mutationFn: ({ itemId, file, mode = 'arquivo' }: { itemId: number; file: File; mode?: 'arquivo' | 'cedula' }) => {
      const body = new FormData();
      if (mode === 'cedula') {
        body.append('cedula', file);
        body.append('etapa_cedula', 'comprovacao');
        body.append('checklist_item_id', String(itemId));
        return api(`/api/v2/processos-credito/${cardId}/cedula/extrair`, {
          method: 'POST',
          body,
          schema: z.unknown(),
        });
      }
      body.append('arquivo', file);
      return api(`/api/v2/processos-credito/${cardId}/checklist/${itemId}/upload`, {
        method: 'POST',
        body,
        schema: checklistItemSchema,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['credito-checklist', cardId] }),
  });

  if (isLoading) {
    return <p className="text-sm text-text-muted py-4">Carregando checklist…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-danger py-4">
        Falha ao carregar checklist de documentos.
      </p>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="text-sm text-text-muted py-4">
        Nenhum item de checklist configurado para este pipe.
      </p>
    );
  }

  // Agrupa por bloco mantendo ordem
  const blocos = new Map<string, { nome: string; ordem: number; items: ChecklistItem[] }>();
  for (const it of data) {
    const codigo = it.bloco_codigo ?? 'outros';
    const nome = it.bloco_nome ?? 'Outros documentos';
    const ordem = it.bloco_ordem ?? 999;
    if (!blocos.has(codigo)) blocos.set(codigo, { nome, ordem, items: [] });
    blocos.get(codigo)!.items.push(it);
  }
  const ordered = Array.from(blocos.entries()).sort(
    ([, a], [, b]) => a.ordem - b.ordem,
  );

  // Resumo de progresso
  const total = data.length;
  const recebidos = data.filter(
    (i) => i.status === 'recebido' || i.status === 'aprovado' || i.status === 'validado',
  ).length;
  const obrigatoriosPendentes = data.filter(
    (i) =>
      i.obrigatorio &&
      i.status !== 'recebido' &&
      i.status !== 'aprovado' &&
      i.status !== 'validado' &&
      i.status !== 'nao_aplicavel' &&
      i.status !== 'dispensado',
  ).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <Chip tone="neutral" size="sm">
          {recebidos}/{total} recebidos
        </Chip>
        {obrigatoriosPendentes > 0 && (
          <Chip tone="warn" size="sm">
            {obrigatoriosPendentes} obrigatório{obrigatoriosPendentes > 1 ? 's' : ''} pendente{obrigatoriosPendentes > 1 ? 's' : ''}
          </Chip>
        )}
      </div>
      {upload.error && (
        <p className="text-sm text-danger">
          Não foi possível anexar o documento: {upload.error.message}
        </p>
      )}

      {ordered.map(([codigo, bloco]) => (
        <section
          key={codigo}
          className="border border-border rounded-lg p-4 bg-surface-2"
        >
          <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted mb-3">
            {bloco.nome}
          </h3>
          <ul className="space-y-2">
            {bloco.items.map((it) => {
              const recebido = it.status === 'recebido' || it.status === 'aprovado' || it.status === 'validado';
              const aceitaCedulaOuCronograma = /^Cédula ou cronograma de desembolso$/i.test(it.nome_documento);
              const StatusIcon = recebido
                ? CheckCircle2
                : it.status === 'rejeitado' || it.status === 'vencido'
                ? AlertCircle
                : Circle;
              return (
                <li
                  key={it.id}
                  className="flex items-start gap-3 text-sm py-1.5 border-b border-border last:border-0"
                >
                  <StatusIcon
                    size={16}
                    strokeWidth={1.5}
                    className={
                      recebido
                        ? 'text-emerald-500 shrink-0 mt-0.5'
                        : it.status === 'rejeitado' || it.status === 'vencido'
                        ? 'text-danger shrink-0 mt-0.5'
                        : 'text-text-muted shrink-0 mt-0.5'
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-text break-words">
                        {it.nome_documento}
                      </span>
                      {it.obrigatorio && (
                        <Chip tone="neutral" size="sm">
                          obrigatório
                        </Chip>
                      )}
                      {it.status && it.status !== 'pendente' && (
                        <Chip tone={statusTone(it.status)} size="sm">
                          {statusLabel(it.status)}
                        </Chip>
                      )}
                    </div>
                    {(it.origem_documento || it.data_recebimento || it.vencimento || it.responsavel_conferencia || it.observacoes || it.pendencia || it.documento_origem_id || it.documento_vinculado_id || it.local_origem || it.local_destino || it.data_copia || it.autor_movimentacao) && (
                      <div className="text-xs text-text-muted mt-0.5 flex flex-wrap gap-x-3">
                        {it.origem_documento && (
                          <span>Origem {it.origem_documento}</span>
                        )}
                        {it.data_recebimento && (
                          <span>Recebido em {formatDateBR(it.data_recebimento)}</span>
                        )}
                        {(it.vencimento || it.validade) && (
                          <span>Vencimento {formatDateBR(it.vencimento ?? it.validade ?? '')}</span>
                        )}
                        {it.responsavel_conferencia && (
                          <span>Conferência {it.responsavel_conferencia}</span>
                        )}
                        {it.data_copia && (
                          <span>Espelhado em {formatDateBR(it.data_copia)}</span>
                        )}
                        {it.autor_movimentacao && (
                          <span>Autor {it.autor_movimentacao}</span>
                        )}
                        {(it.documento_origem_id || it.documento_vinculado_id) && (
                          <span>
                            Doc origem {it.documento_origem_id ?? '—'} / vínculo {it.documento_vinculado_id ?? '—'}
                          </span>
                        )}
                        {it.local_origem && (
                          <span>De {it.local_origem}</span>
                        )}
                        {it.local_destino && (
                          <span>Para {it.local_destino}</span>
                        )}
                        {it.observacoes && (
                          <span className="break-words">{it.observacoes}</span>
                        )}
                        {it.pendencia && (
                          <span className="break-words text-amber-700">Pendência: {it.pendencia}</span>
                        )}
                      </div>
                    )}
                    {it.arquivo_url && (
                      <a
                        href={it.arquivo_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-accent inline-flex items-center gap-1 mt-1 hover:underline"
                      >
                        <FileText size={12} strokeWidth={1.5} />
                        Abrir arquivo
                      </a>
                    )}
                    <div className="inline-flex flex-wrap items-center gap-3 mt-1 ml-3">
                      {aceitaCedulaOuCronograma && (
                        <label
                          className={`text-xs inline-flex items-center gap-1 cursor-pointer hover:underline ${
                            upload.isPending ? 'text-text-muted pointer-events-none' : 'text-accent'
                          }`}
                        >
                          {upload.isPending && upload.variables?.itemId === it.id && upload.variables?.mode === 'cedula'
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Upload size={12} strokeWidth={1.5} />}
                          Anexar cédula e extrair dados
                          <input
                            type="file"
                            className="sr-only"
                            accept=".pdf,.png,.jpg,.jpeg"
                            disabled={upload.isPending}
                            onChange={(event) => {
                              const file = event.currentTarget.files?.[0];
                              if (file) upload.mutate({ itemId: it.id, file, mode: 'cedula' });
                              event.currentTarget.value = '';
                            }}
                          />
                        </label>
                      )}
                      <label
                        className={`text-xs inline-flex items-center gap-1 cursor-pointer hover:underline ${
                          upload.isPending ? 'text-text-muted pointer-events-none' : 'text-accent'
                        }`}
                      >
                        {upload.isPending && upload.variables?.itemId === it.id && upload.variables?.mode !== 'cedula'
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Upload size={12} strokeWidth={1.5} />}
                        {aceitaCedulaOuCronograma
                          ? 'Anexar somente cronograma'
                          : it.arquivo_url ? 'Substituir arquivo' : 'Anexar arquivo'}
                        <input
                          type="file"
                          className="sr-only"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                          disabled={upload.isPending}
                          onChange={(event) => {
                            const file = event.currentTarget.files?.[0];
                            if (file) upload.mutate({ itemId: it.id, file });
                            event.currentTarget.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
